import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { translations } from "../i18n/translations";

const LanguageContext = createContext(null);

const detectBrowserLanguage = () => {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem("fk-lang");
    if (stored === "en" || stored === "es") return stored;
  } catch (_e) {
    // ignore
  }
  const langs = [
    ...(navigator.languages || []),
    navigator.language || "",
    navigator.userLanguage || "",
  ]
    .filter(Boolean)
    .map((l) => String(l).toLowerCase());
  const hasSpanish = langs.some((l) => l.startsWith("es"));
  return hasSpanish ? "es" : "en";
};

export const LanguageProvider = ({ children }) => {
  // Lazy init: read browser language before first render.
  const [lang, setLang] = useState(() => detectBrowserLanguage());
  const hydrated = useRef(false);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
    if (!hydrated.current) {
      // Skip the very first effect tick so we don't overwrite a
      // stored or detected value before the user has done anything.
      hydrated.current = true;
      return;
    }
    try {
      window.localStorage.setItem("fk-lang", lang);
    } catch (_e) {
      // ignore
    }
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang: (l) => {
        hydrated.current = true;
        setLang(l);
      },
      t: translations[lang] || translations.en,
    }),
    [lang],
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
};
